import { DataProvider, fetchUtils } from 'react-admin';
import { tenantService } from '../services/tenantService';

const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

// Code table resources mapping
export const codeTableResourceMapping: Record<string, string> = {
  'vehicle-utilizations': 'vehicle-utilizations',
  commodities: 'vehicle-commodities',
};

export const codeTableResources = Object.keys(codeTableResourceMapping);

// HTTP client with authentication and tenant headers
const httpClient = async (url: string, options: any = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: 'application/json' });
  }

  // Only set Content-Type if not using FormData
  if (
    !(options.body instanceof FormData) &&
    options.method !== 'GET' &&
    options.method !== 'DELETE'
  ) {
    options.headers.set('Content-Type', 'application/json');
  }

  const accessToken = localStorage.getItem('accessToken');
  if (accessToken) {
    options.headers.set('Authorization', `Bearer ${accessToken}`);
  }

  // Add tenant code header (similar to fortress-web approach)
  try {
    const tenantCode = tenantService.getCurrentTenantCode();
    options.headers.set('x-tenant-code', tenantCode);
  } catch (error) {
    throw new Error('No tenant selected. Please select a tenant to continue.');
  }

  try {
    const response = await fetchUtils.fetchJson(url, options);
    return response;
  } catch (error: any) {
    if (error.status === 401) {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    throw error;
  }
};

export const codeTableDataProvider: DataProvider = {
  getList: async (resource, _params) => {
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching code table ${resource} list:`, error);
      throw error;
    }
  },

  getOne: async (resource, params) => {
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}/${params.id}`;

    try {
      const { json } = await httpClient(url);
      return { data: json };
    } catch (error) {
      console.error(`Error fetching code table ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  getMany: async (resource, params) => {
    try {
      const codeTableResource = codeTableResourceMapping[resource];
      const promises = params.ids.map(id =>
        httpClient(`${baseUrl}/code-tables/${codeTableResource}/${id}`)
          .then(({ json }) => json)
          .catch(() => null)
      );

      const results = await Promise.all(promises);
      const data = results.filter(item => item !== null);
      return { data };
    } catch (error) {
      console.error(`Error fetching multiple code table ${resource}:`, error);
      return { data: [] };
    }
  },

  getManyReference: async (resource, _params) => {
    // Code table resources don't use getManyReference pattern - just use getList
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}`;

    try {
      const { json, headers } = await httpClient(url);

      let data, total;

      if (Array.isArray(json)) {
        data = json;
        total = parseInt(headers.get('X-Total-Count') || json.length.toString(), 10);
      } else {
        data = json.data || json;
        total = json.total || parseInt(headers.get('X-Total-Count') || '0', 10);
      }

      return { data, total };
    } catch (error) {
      console.error(`Error fetching code table ${resource} references:`, error);
      return { data: [], total: 0 };
    }
  },

  create: async (resource, params) => {
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}`;

    try {
      const { json } = await httpClient(url, {
        method: 'POST',
        body: JSON.stringify(params.data),
      });

      return {
        data: { ...params.data, ...json },
      };
    } catch (error) {
      console.error(`Error creating code table ${resource}:`, error);
      throw error;
    }
  },

  update: async (resource, params) => {
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'PATCH',
        body: JSON.stringify(params.data),
      });

      return { data: json };
    } catch (error) {
      console.error(`Error updating code table ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  updateMany: async (resource, params) => {
    try {
      const codeTableResource = codeTableResourceMapping[resource];
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/code-tables/${codeTableResource}/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(params.data),
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error updating multiple code table ${resource}:`, error);
      throw error;
    }
  },

  delete: async (resource, params) => {
    const codeTableResource = codeTableResourceMapping[resource];
    const url = `${baseUrl}/code-tables/${codeTableResource}/${params.id}`;

    try {
      const { json } = await httpClient(url, {
        method: 'DELETE',
      });

      return { data: json };
    } catch (error) {
      console.error(`Error deleting code table ${resource}/${params.id}:`, error);
      throw error;
    }
  },

  deleteMany: async (resource, params) => {
    try {
      const codeTableResource = codeTableResourceMapping[resource];
      await Promise.all(
        params.ids.map(id =>
          httpClient(`${baseUrl}/code-tables/${codeTableResource}/${id}`, {
            method: 'DELETE',
          })
        )
      );

      return { data: params.ids };
    } catch (error) {
      console.error(`Error deleting multiple code table ${resource}:`, error);
      throw error;
    }
  },
};
